<template>
	<div class="category-nav navigation-category-categoryTree">
		<nav class="category-nav" role="navigation" aria-label="Kategorien">
			<template v-if="categoryTree?.length">
				<div class="category-list" v-for="category in categoryTree" :key="category.extId">
					<NuxtLink :to="basedir + category.url" :title="category.name" target="_self" class="nav-link">
						<component :is="isMainCategory(category) ? 'h1' : 'span'" class="text-xl font-bold" style="font-family: 'Lato';">
							{{ category.name }}
						</component>
					</NuxtLink>
					<div class="overflow-hidden transition-all duration-300" :style="{ maxHeight: getChildrenMaxHeight(category) }">
						<template v-for="childcategory in visibleChildren(category)" :key="childcategory.extId">
							<div
								class="category-list"
								:ref="(el) => setChildRowRef(getChildRowKey(category.extId, childcategory.extId), el as HTMLElement | null)">
								<template
									v-if="
										childcategory.extId !== mainCategory?.categoryId &&
										childcategory.descendants?.find((gcc) => gcc.extId !== mainCategory?.categoryId) === undefined
									">
									<NuxtLink
										:to="`${basedir}${category.url}/${childcategory.url}`"
										:title="childcategory.name"
										target="_self"
										class="nav-link hyphens-auto">
										{{ childcategory.name }}
									</NuxtLink>
								</template>
								<template v-else>
									<NuxtLink
										class="nav-link flex justify-between py-1 items-center"
										:to="`${basedir}${category.url}/${childcategory.url}`"
										:title="childcategory.name"
										target="_self">
										<span class="level-1">{{ childcategory.name }}</span>

										<template v-if="childcategory.descendants?.length">
											<span
												class="inline-flex"
												@click.prevent="openDropdown === childcategory.id ? (openDropdown = '0') : (openDropdown = childcategory.id)">
												<BasicIcon
													v-if="showChevron"
													:name="
														childcategory.url === route.params.childCategory || openDropdown === childcategory.id
															? 'chevron-down'
															: 'chevron-right'
													"
													:icon-class="'w-5 h-5'" />
											</span>
										</template>
									</NuxtLink>

									<div
										class="category-list last childs"
										:class="childcategory.url === route.params.childCategory || openDropdown === childcategory.id ? 'open' : 'close'">
										<div
											class="overflow-hidden transition-all duration-300"
											:style="{ maxHeight: getGrandchildrenMaxHeight(childcategory) }">
											<template v-for="grandchildcategory in visibleGrandchildren(childcategory)" :key="grandchildcategory.extId">
												<div
													:ref="
														(el) =>
															setGrandchildRowRef(
																getGrandchildRowKey(childcategory.extId, grandchildcategory.extId),
																el as HTMLElement | null,
															)
													">
													<NuxtLink
														:to="`${basedir}${category.url}/${childcategory.url}/${grandchildcategory.url}`"
														:title="grandchildcategory.name"
														target="_self"
														class="nav-link py-1 hyphens-auto level-2">
														{{ grandchildcategory.name }}
													</NuxtLink>
												</div>
											</template>
										</div>

										<div class="mt-1" v-if="childcategory.descendants?.length">
											<button
												v-if="remainingGrandchildren(childcategory) > 0 && !isGrandExpanded(childcategory.extId)"
												class="text-sm text-primary hover:underline"
												:aria-expanded="isGrandExpanded(childcategory.extId)"
												@click="toggleGrand(childcategory.extId)">
												Zeige {{ remainingGrandchildren(childcategory) }} weitere
											</button>
											<button
												v-else-if="isGrandExpanded(childcategory.extId)"
												class="text-sm text-primary hover:underline"
												:aria-expanded="isGrandExpanded(childcategory.extId)"
												@click="toggleGrand(childcategory.extId)">
												Zeige weniger
											</button>
										</div>
									</div>
								</template>
							</div>
						</template>
					</div>

					<div class="mt-1 category-list" v-if="category.descendants?.length">
						<button
							v-if="remainingChildren(category) > 0 && !isChildrenExpanded(category.extId)"
							class="text-sm text-primary hover:underline"
							:aria-expanded="isChildrenExpanded(category.extId)"
							@click="toggleChildren(category.extId)">
							Zeige {{ remainingChildren(category) }} weitere
						</button>
						<button
							v-else-if="isChildrenExpanded(category.extId)"
							class="text-sm text-primary hover:underline"
							:aria-expanded="isChildrenExpanded(category.extId)"
							@click="toggleChildren(category.extId)">
							Zeige weniger
						</button>
					</div>
				</div>
			</template>
		</nav>
	</div>
</template>

<script setup lang="ts">
	import type { NeoArticleCategoryView } from '@expert/types';
  import { inject, ref, nextTick, onMounted, onBeforeUnmount } from "vue";
	import { ProvideNames } from '#shared/statics';
  import type { MenuLink } from '~~/models/Navigation';

	interface Props {
		mainCategory: NeoArticleCategoryView | undefined;
    categoryTree: Array<MenuLink>;
		showNeighbors?: boolean;
		showChevron?: boolean;
	}

	withDefaults(defineProps<Props>(), {
		showNeighbors: false,
		showChevron: false,
	});

	const route = useRoute();

	const CHILD_LIMIT = 4;
	const GRANDCHILD_LIMIT = 4;
	const FULL_HEIGHT = "100rem";
	const FALLBACK_CHILDREN_HEIGHT = "16rem";
	const FALLBACK_GRANDCHILDREN_HEIGHT = "7.5rem";
	const TRANSITION_SYNC_DELAY = 280;

	const expandedChildren = ref<Record<string, boolean>>({});
	const expandedGrandchildren = ref<Record<string, boolean>>({});
	const childRowRefs = ref<Record<string, HTMLElement | null>>({});
	const grandchildRowRefs = ref<Record<string, HTMLElement | null>>({});
	const layoutVersion = ref(0);
	const openDropdown = ref("0");

	const basedir = inject(ProvideNames.BASE_DIR) as string;

	const refreshLayout = async () => {
		await nextTick();
		layoutVersion.value++;

		setTimeout(() => {
			layoutVersion.value++;
		}, TRANSITION_SYNC_DELAY);
	};

	const handleResize = () => {
		layoutVersion.value++;
	};

	const isMainCategory = (category: CategoryNode) => {
		const segments = route.path.split("/").filter(Boolean);
		return segments[segments.length - 1] === category.url;
	};

	onMounted(() => {
		window.addEventListener("resize", handleResize);
		refreshLayout();
	});

	onBeforeUnmount(() => {
		window.removeEventListener("resize", handleResize);
	});

	const isChildrenExpanded = (parentId?: string) => Boolean(parentId && expandedChildren.value[parentId]);
	const isGrandExpanded = (childId?: string) => Boolean(childId && expandedGrandchildren.value[childId]);

	const toggleChildren = async (parentId?: string) => {
		if (!parentId) return;
		expandedChildren.value[parentId] = !expandedChildren.value[parentId];
		await refreshLayout();
	};

	const toggleGrand = async (childId?: string) => {
		if (!childId) return;
		expandedGrandchildren.value[childId] = !expandedGrandchildren.value[childId];
		await refreshLayout();
	};

	const getChildRowKey = (categoryExtId?: string, childExtId?: string) => `${categoryExtId ?? ""}::${childExtId ?? ""}`;
	const getGrandchildRowKey = (childExtId?: string, grandchildExtId?: string) => `${childExtId ?? ""}::${grandchildExtId ?? ""}`;

	const setChildRowRef = (rowKey?: string, el?: HTMLElement | null) => {
		if (!rowKey) return;
		childRowRefs.value[rowKey] = el ?? null;
	};

	const setGrandchildRowRef = (rowKey?: string, el?: HTMLElement | null) => {
		if (!rowKey) return;
		grandchildRowRefs.value[rowKey] = el ?? null;
	};

	const isActiveChild = (child: CategoryNode) => child.url === route.params.childCategory;
	const isActiveGrandchild = (grandchild: CategoryNode) => grandchild.url === route.params.productType;

	const sortActiveFirst = (items: CategoryNode[], isActive: (item: CategoryNode) => boolean) =>
		[...items].sort((a, b) => Number(isActive(b)) - Number(isActive(a)));

	const visibleChildren = (parent: CategoryNode) => sortActiveFirst(parent.descendants || [], isActiveChild);
	const visibleGrandchildren = (child: CategoryNode) => sortActiveFirst(child.descendants || [], isActiveGrandchild);

	const sumHeights = (keys: string[], refsMap: Record<string, HTMLElement | null>) => keys.reduce((sum, key) => sum + (refsMap[key]?.offsetHeight || 0), 0);

	const pxOrFallback = (height: number, fallback: string) => (height > 0 ? `${height}px` : fallback);

	const getCollapsedChildrenHeight = (category: CategoryNode) => {
		layoutVersion.value;

		const keys = visibleChildren(category)
			.slice(0, CHILD_LIMIT)
			.map((child) => getChildRowKey(category.extId, child.extId));

		return sumHeights(keys, childRowRefs.value);
	};

	const getCollapsedGrandchildrenHeight = (child: CategoryNode) => {
		layoutVersion.value;

		const keys = visibleGrandchildren(child)
			.slice(0, GRANDCHILD_LIMIT)
			.map((grandchild) => getGrandchildRowKey(child.extId, grandchild.extId));

		return sumHeights(keys, grandchildRowRefs.value);
	};

	const getFullGrandchildrenHeight = (child: CategoryNode) => {
		layoutVersion.value;

		const keys = visibleGrandchildren(child).map((grandchild) => getGrandchildRowKey(child.extId, grandchild.extId));

		return sumHeights(keys, grandchildRowRefs.value);
	};

	const getChildrenMaxHeight = (category: CategoryNode) =>
		isChildrenExpanded(category.extId) ? FULL_HEIGHT : pxOrFallback(getCollapsedChildrenHeight(category), FALLBACK_CHILDREN_HEIGHT);

	const getGrandchildrenMaxHeight = (child: CategoryNode) =>
		isGrandExpanded(child.extId)
			? pxOrFallback(getFullGrandchildrenHeight(child), FULL_HEIGHT)
			: pxOrFallback(getCollapsedGrandchildrenHeight(child), FALLBACK_GRANDCHILDREN_HEIGHT);

	const remainingChildren = (parent: CategoryNode) => Math.max(0, (parent.descendants?.length || 0) - CHILD_LIMIT);
	const remainingGrandchildren = (child: CategoryNode) => Math.max(0, (child.descendants?.length || 0) - GRANDCHILD_LIMIT);
</script>

<style scoped>
	.category-nav > .category-list,
	.category-nav > .category-list button,
	.category-nav > .category-list .category-list {
		font-size: 0.75rem;
	}
	.category-nav .category-list:has(> .open) .level-1,
	.category-nav .router-link-active .level-1{
		@apply text-lg;
		@apply font-bold;
	}

</style>
